import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ds";
import { CleanerSchedule } from "@/components/app/rota/CleanerSchedule";
import { usePageTitle } from "../hooks/usePageTitle";

interface CheckInRecord {
  readonly recordedAt: string;
  readonly verification: string;
}

interface TodayShift {
  readonly id: string;
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly siteName: string;
  readonly siteAddress: string | null;
  readonly sitePostcode: string | null;
  readonly siteNotes: string | null;
  readonly siteLatitude: number | null;
  readonly siteLongitude: number | null;
  readonly siteGpsRadiusMetres: number;
  readonly checkIn: CheckInRecord | null;
  readonly checkOut: CheckInRecord | null;
}

interface UpcomingShift {
  readonly id: string;
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly siteName: string;
  readonly siteAddress: string | null;
  readonly siteNotes: string | null;
}

function ScheduleSkeleton() {
  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "var(--space-4)" }}>
      <Skeleton width="60%" height="0.75rem" borderRadius="var(--radius-sm)" />
      <div style={{ marginTop: "var(--space-3)" }}>
        <Skeleton width="100%" height="10rem" borderRadius="var(--radius-xl)" />
      </div>
      <div style={{ marginTop: "var(--space-3)" }}>
        <Skeleton width="100%" height="10rem" borderRadius="var(--radius-xl)" />
      </div>
      <div style={{ marginTop: "var(--space-7)" }}>
        <Skeleton width="40%" height="0.75rem" borderRadius="var(--radius-sm)" />
      </div>
      <div style={{ marginTop: "var(--space-3)" }}>
        <Skeleton width="100%" height="3rem" borderRadius="var(--radius-md)" />
      </div>
      <div style={{ marginTop: "var(--space-2)" }}>
        <Skeleton width="100%" height="3rem" borderRadius="var(--radius-md)" />
      </div>
    </div>
  );
}

export function MyPage() {
  usePageTitle("My Schedule | TidyRota");
  const [todayShifts, setTodayShifts] = useState<TodayShift[] | null>(null);
  const [upcomingShifts, setUpcomingShifts] = useState<UpcomingShift[]>([]);
  const [todayDate, setTodayDate] = useState("");

  useEffect(() => {
    Promise.all([
      fetch("/api/my/today").then((r) => r.json()),
      fetch("/api/my/schedule").then((r) => r.json()),
    ]).then(([todayResult, scheduleResult]) => {
      if (todayResult.success) {
        setTodayShifts(todayResult.data.shifts);
        setTodayDate(todayResult.data.date);
      }
      if (scheduleResult.success) {
        const today = todayResult.data?.date ?? new Date().toISOString().split("T")[0];
        const future = scheduleResult.data.shifts.filter(
          (s: UpcomingShift) => s.date > today,
        );
        setUpcomingShifts(future);
      }
    });
  }, []);

  if (todayShifts === null) {
    return <ScheduleSkeleton />;
  }

  return (
    <CleanerSchedule
      todayShifts={todayShifts}
      upcomingShifts={upcomingShifts}
      todayDate={todayDate}
    />
  );
}
